import { useEffect, useRef } from 'react'

import type OpenAI from 'openai'

import type { ActiveToolCall, PendingToolCall } from '../../core/engine/types'

export function useScrollAnchor(
  messages: OpenAI.ChatCompletionMessageParam[],
  activeToolCalls: ActiveToolCall[],
  pendingToolCalls: PendingToolCall[],
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const userScrolledUp = useRef(false)
  const prevLengthRef = useRef(0)
  const rafRef = useRef<number | null>(null)
  const isProgrammaticScroll = useRef(false)

  // Reset scroll lock when user submits a new message
  useEffect(() => {
    if (messages.length > prevLengthRef.current) {
      const last = messages[messages.length - 1]
      if (last?.role === 'user') {
        userScrolledUp.current = false
      }
      prevLengthRef.current = messages.length
    }
  }, [messages])

  // Auto-scroll to bottom on new messages or tool call changes
  useEffect(() => {
    if (userScrolledUp.current) return
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      const el = containerRef.current
      if (el) {
        isProgrammaticScroll.current = true
        el.scrollTop = el.scrollHeight
        requestAnimationFrame(() => {
          isProgrammaticScroll.current = false
        })
      }
      rafRef.current = null
    })
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [messages, activeToolCalls, pendingToolCalls])

  // Keep scrolling as typewriter drains — fires on every text node change inside the container
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new MutationObserver(() => {
      if (userScrolledUp.current) return
      el.scrollTop = el.scrollHeight
    })
    observer.observe(el, {
      subtree: true,
      characterData: true,
      childList: true,
    })
    return () => observer.disconnect()
  }, [])

  const handleScroll = () => {
    if (isProgrammaticScroll.current) return
    const el = containerRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50
    userScrolledUp.current = !atBottom
  }

  return { containerRef, handleScroll }
}
